import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Navigate } from "react-router-dom";
import {
  useRevenueSummary,
  useRevenueTimeseries,
  useProducts,
  useSubscriptionsSummary,
  useUsageSummary,
  useUsageTimeseries,
  useSubscriptionsTimeseries,
} from "../hooks/useAppAnalytics";
import { AppPlatform } from "../types";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/shared/ui/select";
import { DateRangePicker } from "@/shared/widgets/DateRangePicker";
import { DateRange } from "react-day-picker";
import { PageContent } from "@/shared/widgets/layout/PageContent";
import { TooltipProvider } from "@/shared/ui/tooltip";
import { RevenueTabContent } from "./components/RevenueTabContent";
import { SubscriptionsTabContent } from "./components/SubscriptionsTabContent";
import { UsageTabContent } from "./components/UsageTabContent";
import { useAuth } from "@/features/auth/model/AuthContext";
import { UserRole } from "@/shared/types/roles";
import { AlertCircle } from "lucide-react";

const normalizeDate = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12, 0, 0, 0);

const formatDateParam = (date: Date) => {
  const normalized = normalizeDate(date);
  const year = normalized.getFullYear();
  const month = String(normalized.getMonth() + 1).padStart(2, "0");
  const day = String(normalized.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const parseDateParam = (value: string) => {
  const [yearStr, monthStr, dayStr] = value.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr);
  const day = Number(dayStr);
  return normalizeDate(new Date(year, month - 1, day));
};

type TabKey = "revenue" | "subscriptions" | "usage";

export default function AppAnalyticsPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Verifica se o usuário é admin
  if (user?.role !== UserRole.ADMIN) {
    return <Navigate to="/unauthorized" replace />;
  }

  const selectedProduct = searchParams.get("product") || "all";
  const activeTab = (searchParams.get("tab") as TabKey) || "revenue";

const [dateRange, setDateRange] = useState<DateRange | undefined>(() => {
    const startParam = searchParams.get("start");
    const endParam = searchParams.get("end");

    if (startParam && endParam) {
      return { from: parseDateParam(startParam), to: parseDateParam(endParam) };
    }

    const end = new Date();
    const start = new Date();
    start.setMonth(start.getMonth() - 3);
    return { from: normalizeDate(start), to: normalizeDate(end) };
  });

  const updateURL = (updates: Record<string, string | null>) => {
    const newParams = new URLSearchParams(searchParams);

    Object.entries(updates).forEach(([key, value]) => {
      if (value === null || value === "all" || value === "revenue") {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });

    setSearchParams(newParams);
  };

  const handleProductChange = (product: string) => {
    updateURL({ product: product === "all" ? null : product });
  };

  const handleTabChange = (tab: TabKey) => {
    updateURL({ tab: tab === "revenue" ? null : tab });
  };

  const handleDateRangeChange = (range: DateRange | undefined) => {
    const normalizedRange = range
      ? {
          from: range.from ? normalizeDate(range.from) : undefined,
          to: range.to ? normalizeDate(range.to) : undefined,
        }
      : undefined;

    setDateRange(normalizedRange);

    if (normalizedRange?.from && normalizedRange?.to) {
      updateURL({
        start: formatDateParam(normalizedRange.from),
        end: formatDateParam(normalizedRange.to),
      });
    } else {
      updateURL({ start: null, end: null });
    }
  };

useEffect(() => {
    const startParam = searchParams.get("start");
    const endParam = searchParams.get("end");

    if (startParam && endParam) {
      setDateRange({ from: parseDateParam(startParam), to: parseDateParam(endParam) });
    }
  }, [searchParams]);

const range = useMemo(() => {
    if (!dateRange?.from || !dateRange?.to) {
      const end = new Date();
      const start = new Date();
      start.setMonth(start.getMonth() - 3);
      return {
        start: formatDateParam(start),
        end: formatDateParam(end),
      };
    }

    return {
      start: formatDateParam(dateRange.from),
      end: formatDateParam(dateRange.to),
    };
  }, [dateRange]);

  const { data: products, isLoading: loadingProducts } = useProducts();

  const revenueQueryParams = {
    start: range.start,
    end: range.end,
    platform: selectedProduct as AppPlatform,
  };

  const { data: revenueSummary, isLoading: loadingRevenueSummary } = useRevenueSummary(revenueQueryParams);
  const { data: revenueTimeseries, isLoading: loadingRevenueTimeseries } = useRevenueTimeseries(revenueQueryParams);

  const subscriptionsQueryParams = { ...revenueQueryParams };
  const { data: subscriptionsSummary, isLoading: loadingSubscriptionsSummary } =
    useSubscriptionsSummary(subscriptionsQueryParams);
  const { data: subscriptionsTimeseries, isLoading: loadingSubscriptionsTimeseries } =
    useSubscriptionsTimeseries(subscriptionsQueryParams);

  const usageQueryParams = revenueQueryParams;
  const { data: usageSummary, isLoading: loadingUsageSummary } = useUsageSummary(usageQueryParams);
  const { data: usageTimeseries, isLoading: loadingUsageTimeseries } = useUsageTimeseries(usageQueryParams);

  return (
    <TooltipProvider>
      <PageContent
        title="Revenue Analytics"
        items={[
          { label: "Dashboard", href: "/" },
          { label: "Revenue Analytics", href: "/app-analytics" },
        ]}
      >
        <div className="min-h-screen bg-[#1A1A1A] text-white relative">
          {/* Banner de Aviso BETA */}
          <div className="mb-6 bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-amber-500 font-semibold mb-1">Funcionalidade em BETA</h3>
                <p className="text-sm text-white/70">
                  Esta página está em fase de testes e desenvolvimento. Os dados exibidos podem conter inconsistências 
                  e estão sendo validados. Acesso restrito apenas para administradores durante o período de beta.
                </p>
              </div>
            </div>
          </div>

          <div className="sticky top-16 z-20 bg-[#1A1A1A] py-2 mb-4">
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 items-start sm:items-center">
              <div className="flex flex-wrap gap-2 sm:gap-3 items-center">
                <div className="min-w-[200px]">
                  <label className="text-sm font-medium text-white/60 mb-2 block">Produto</label>
                  <Select value={selectedProduct} onValueChange={handleProductChange} disabled={loadingProducts}>
                    <SelectTrigger className="w-full bg-[#232323] border-white/10 text-white">
                      <SelectValue placeholder="Selecionar produto" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#232323] border-white/10">
                      <SelectItem value="all">Todos os produtos</SelectItem>
                      {products &&
                        Object.entries(products).map(([key, product]) => (
                          <SelectItem key={key} value={product.id.toString()}>
                            <div className="flex items-center gap-2">
                              <img
                                src={product.icon}
                                alt={product.name}
                                className="w-4 h-4 rounded"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = "none";
                                }}
                              />
                              <span>{product.name}</span>
                              <span className="text-xs text-white/40">({product.store})</span>
                            </div>
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="min-w-[280px]">
                  <label className="text-sm font-medium text-white/60 mb-2 block">Período</label>
                  <DateRangePicker dateRange={dateRange} setDateRange={handleDateRangeChange} />
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <div className="inline-flex rounded-lg bg-[#232323] p-1 border border-white/10">
              <button
                onClick={() => handleTabChange("revenue")}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  activeTab === "revenue" ? "bg-white/10 text-white" : "text-white/60 hover:text-white"
                }`}
              >
                Revenue
              </button>
              <button
                onClick={() => handleTabChange("subscriptions")}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  activeTab === "subscriptions" ? "bg-white/10 text-white" : "text-white/60 hover:text-white"
                }`}
              >
                Subscriptions
              </button>
              <button
                onClick={() => handleTabChange("usage")}
                className={`px-4 py-2 rounded-md text-sm font-medium ${
                  activeTab === "usage" ? "bg-white/10 text-white" : "text-white/60 hover:text-white"
                }`}
              >
                Usage
              </button>
            </div>
          </div>

          {activeTab === "revenue" && (
            <RevenueTabContent
              loadingSummary={loadingRevenueSummary}
              revenueSummary={revenueSummary}
              loadingRevenueTimeseries={loadingRevenueTimeseries}
              revenueTimeseries={revenueTimeseries}
              range={range}
            />
          )}

          {activeTab === "subscriptions" && (
            <SubscriptionsTabContent
              loadingSummary={loadingSubscriptionsSummary}
              subscriptionsSummary={subscriptionsSummary}
              loadingSubscriptionsTimeseries={loadingSubscriptionsTimeseries}
              subscriptionsTimeseries={subscriptionsTimeseries}
            />
          )}

          {activeTab === "usage" && (
            <UsageTabContent
              loadingSummary={loadingUsageSummary}
              usageSummary={usageSummary}
              loadingUsageTimeseries={loadingUsageTimeseries}
              usageTimeseries={usageTimeseries}
            />
          )}
        </div>
      </PageContent>
    </TooltipProvider>
  );
}
